import { useEffect, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import {
  Match,
  PutRequest,
  useDeleteServiceKindMatchMutation,
  useListAxDienstleistungQuery,
  usePutServiceKindMatchMutation,
  useShowServiceKindMatchQuery,
} from "@/services/backend/dynamics/api/service-kind-match";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { H3 } from "@/shared/components/typography";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { ServiceKindList } from "@/services/backend/services/types/kinds";

export function ServiceKindMatching() {
  return (
    <PersistenceContextProvider>
      <div className="flex items-start justify-between">
        <H3>{t("Dienstleistungen")}</H3>
        <PersistenceStateUsingContext />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("GSM Dienstleistung")}</TableHead>
            <TableHead>{t("Dynamics Dienstleistung")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ServiceKindList.map((serviceKind) => (
            <TableRow key={serviceKind}>
              <TableCell>{serviceKind}</TableCell>
              <TableCell>
                <AxDienstleistung serviceKind={serviceKind} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PersistenceContextProvider>
  );
}

function AxDienstleistung({ serviceKind }: { serviceKind: string }) {
  const {
    data: match,
    isLoading,
    error,
  } = useShowServiceKindMatchQuery({ serviceKind });

  if (isLoading) {
    return <DynamicsServiceKindSelectSkeleton />;
  }

  // 404 is treated as an implicit match, without a newUnternehmensgruppeValue
  if (typeof error === "object" && "status" in error && error.status === 404) {
    return (
      <AxDienstleistungMatch
        match={{ serviceKind, axDienstleistungValue: 0 }}
      />
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <AxDienstleistungMatch match={match!} />;
}

function AxDienstleistungMatch({ match }: { match: Match }) {
  const [request, setRequest] = useState<PutRequest>({
    ...match,
  });
  const mutation = usePutServiceKindMatchMutation();
  const { isLoading } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    { ...match },
    { delay: 0 },
  );

  const [deleteMatch, { isLoading: deleteIsLoading, error: deleteError }] =
    useDeleteServiceKindMatchMutation();

  const doDelete = () => {
    if (deleteIsLoading) {
      return;
    }

    deleteMatch({ serviceKind: match.serviceKind });
  };

  const { toast } = useToast();

  useEffect(() => {
    if (deleteError) {
      toast({
        ...parseRTKQueryError(deleteError),
        variant: "destructive",
      });
    }
  }, [deleteError, toast]);

  return (
    <DynamicsServiceKindSelect
      dynamicsServiceKindId={match.axDienstleistungValue.toString(10)}
      onDynamicsServiceKindIdChange={(axDienstleistungValue) => {
        if (axDienstleistungValue === "0") {
          doDelete();
        } else {
          setRequest({
            ...request,
            axDienstleistungValue: parseInt(axDienstleistungValue, 10),
          });
        }
      }}
      disabled={isLoading || deleteIsLoading}
    />
  );
}

interface DynamicsServiceKindSelectProps {
  dynamicsServiceKindId: string;
  onDynamicsServiceKindIdChange: (dynamicsBranchId: string) => void;
  disabled?: boolean;
}

function DynamicsServiceKindSelect({
  dynamicsServiceKindId,
  onDynamicsServiceKindIdChange,
  disabled = false,
}: DynamicsServiceKindSelectProps) {
  const {
    data: dynamicsServiceKindes,
    isLoading,
    error,
  } = useListAxDienstleistungQuery({});

  if (isLoading) {
    return <DynamicsServiceKindSelectSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Select
      disabled={disabled}
      value={dynamicsServiceKindId}
      onValueChange={onDynamicsServiceKindIdChange}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t("Dynamics Dienstleistung auswählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {dynamicsServiceKindes?.axDienstleistungen.map(
            (dynamicsServiceKind) => (
              <SelectItem
                value={dynamicsServiceKind.value.toString()}
                key={dynamicsServiceKind.value}
              >
                {dynamicsServiceKind.label} ({dynamicsServiceKind.value})
              </SelectItem>
            ),
          )}
        </SelectGroup>
        <SelectSeparator />
        <SelectItem value="0">{t("Keine Zuordnung")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function DynamicsServiceKindSelectSkeleton() {
  return (
    <Select disabled>
      <SelectTrigger className="w-full animate-pulse">
        <SelectValue placeholder={t("Lade...")} />
      </SelectTrigger>
    </Select>
  );
}
